import { usePageTitle } from '../hooks/usePageTitle';
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { clientAPI, phaseAPI, checklistAPI, taskAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { getPhaseLabel, getStatusColor, formatDate } from '../lib/utils';
import {
  CheckCircle2,
  Circle,
  ChevronRight,
  ClipboardList,
  ListTodo,
  FileCheck,
  Lock,
  Loader2,
  Plus,
  RefreshCw,
  Play,
  RotateCcw,
  ShieldCheck,
  AlertTriangle,
  Settings,
  BookOpen,
  Trash2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const PHASE_ORDER = [
  'gap_analysis',
  'application_implementation',
  'desktop_nc_closure',
  'pre_assessment',
  'final_nc_closure',
];

const PHASE_ICONS = {
  gap_analysis: ClipboardList,
  application_implementation: FileCheck,
  desktop_nc_closure: ListTodo,
  pre_assessment: ShieldCheck,
  final_nc_closure: CheckCircle2,
};

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending', icon: Circle, color: 'text-slate-500' },
  { value: 'in_progress', label: 'In Progress', icon: Play, color: 'text-blue-600' },
  { value: 'completed', label: 'Completed', icon: CheckCircle2, color: 'text-green-600' },
  { value: 'blocked', label: 'Blocked', icon: AlertTriangle, color: 'text-red-600' },
];

export const PhaseManagementPage = () => {
  const { isClient, isAdmin, isStaff, user } = useAuth();
  usePageTitle('Phase Management');
  const location = useLocation();
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [phases, setPhases] = useState([]);
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [checklist, setChecklist] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [isAddItemOpen, setIsAddItemOpen] = useState(false);
  const [isStatusChangeOpen, setIsStatusChangeOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [newItem, setNewItem] = useState({ title: '', description: '', category: 'general' });
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [phaseConfig, setPhaseConfig] = useState(null);
  const [configEdits, setConfigEdits] = useState({});
  const [phaseMetadata, setPhaseMetadata] = useState([]);

  useEffect(() => {
    loadClients();
    if (isAdmin()) {
      phaseAPI.getMetadata().then(res => setPhaseMetadata(res.data)).catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (selectedClientId) {
      loadPhases(selectedClientId);
    }
  }, [selectedClientId]);

  useEffect(() => {
    if (selectedPhase) {
      loadPhaseData(selectedPhase.id);
    }
  }, [selectedPhase?.id]);

  const loadClients = async () => {
    try {
      const res = await clientAPI.getAll();
      setClients(res.data);
      // Use clientId from navigation state if provided (from Phase Master)
      const navClientId = location.state?.clientId;
      if (navClientId && res.data.some((c) => c.id === navClientId)) {
        setSelectedClientId(navClientId);
      } else if (res.data.length > 0) {
        setSelectedClientId(res.data[0].id);
      }
    } catch (error) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const loadPhases = async (clientId) => {
    try {
      const res = await phaseAPI.getAll({ client_id: clientId });
      // Filter out archived phases and dedupe by phase_type to prevent duplicate React keys
      const activeOnly = (res.data || []).filter(p => !p.archived);
      const seen = new Set();
      const deduped = activeOnly.filter(p => {
        if (seen.has(p.phase_type)) return false;
        seen.add(p.phase_type);
        return true;
      });
      const sorted = deduped.sort(
        (a, b) => PHASE_ORDER.indexOf(a.phase_type) - PHASE_ORDER.indexOf(b.phase_type)
      );
      setPhases(sorted);
      const active = sorted.find((p) => p.status === 'in_progress') || sorted[0];
      if (active) setSelectedPhase(active);
    } catch (error) {
      toast.error('Failed to load phases');
    }
  };

  const loadPhaseData = async (phaseId) => {
    setChecklistLoading(true);
    try {
      const [checklistRes, tasksRes] = await Promise.all([
        checklistAPI.getAll({ phase_id: phaseId }),
        taskAPI.getAll({ phase_id: phaseId }),
      ]);
      setChecklist(checklistRes.data);
      setTasks(tasksRes.data);
    } catch (error) {
      toast.error('Failed to load phase data');
    } finally {
      setChecklistLoading(false);
    }
  };

  const handleToggleChecklist = async (item) => {
    try {
      await checklistAPI.update(item.id, { is_completed: !item.is_completed });
      loadPhaseData(selectedPhase.id);
      loadPhases(selectedClientId);
    } catch (error) {
      toast.error('Failed to update checklist item');
    }
  };

  const handleGenerateChecklist = async () => {
    if (!selectedPhase) return;
    try {
      const res = await checklistAPI.generateForPhase(selectedPhase.id);
      toast.success(res.data.message);
      loadPhaseData(selectedPhase.id);
    } catch (error) {
      toast.error('Failed to generate checklist');
    }
  };

  const handleAddChecklistItem = async (e) => {
    e.preventDefault();
    try {
      await checklistAPI.create({
        ...newItem,
        phase_id: selectedPhase.id,
        client_id: selectedClientId,
      });
      toast.success('Item added!');
      setIsAddItemOpen(false);
      setNewItem({ title: '', description: '', category: 'general' });
      loadPhaseData(selectedPhase.id);
    } catch (error) {
      toast.error('Failed to add item');
    }
  };

  // --- PHASE STATUS CHANGE with RBAC ---
  const canChangeStatus = () => {
    if (isClient()) return false;
    return true; // Admin and Staff can change (backend enforces further RBAC)
  };

  const getAvailableStatuses = (phase) => {
    if (!phase) return [];
    const currentStatus = phase.status;
    const phaseIdx = PHASE_ORDER.indexOf(phase.phase_type);

    // Admin can set any status (override mode)
    if (isAdmin()) {
      return STATUS_OPTIONS.filter((s) => s.value !== currentStatus);
    }

    // Staff: sequential only
    if (isStaff()) {
      const allowed = [];
      if (currentStatus === 'pending' && phaseIdx === 0) {
        allowed.push('in_progress');
      } else if (currentStatus === 'pending') {
        // Can only start if previous phase completed
        const prevPhase = phases.find((p) => PHASE_ORDER.indexOf(p.phase_type) === phaseIdx - 1);
        if (prevPhase?.status === 'completed') {
          allowed.push('in_progress');
        }
      } else if (currentStatus === 'in_progress') {
        allowed.push('completed');
        allowed.push('blocked');
      } else if (currentStatus === 'blocked') {
        allowed.push('in_progress');
      }
      return STATUS_OPTIONS.filter((s) => allowed.includes(s.value));
    }

    return [];
  };

  const openStatusChange = (phase) => {
    setSelectedPhase(phase);
    setNewStatus('');
    setStatusNote('');
    setIsStatusChangeOpen(true);
  };

  const handleStatusChange = async () => {
    if (!selectedPhase || !newStatus) return;
    try {
      const updateData = { status: newStatus };
      if (statusNote) updateData.notes = statusNote;
      await phaseAPI.update(selectedPhase.id, updateData);
      const statusLabel = STATUS_OPTIONS.find((s) => s.value === newStatus)?.label || newStatus;
      toast.success(`Phase updated to "${statusLabel}"`);
      setIsStatusChangeOpen(false);
      loadPhases(selectedClientId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update phase status');
    }
  };

  const getPhaseStatus = (phase) => {
    if (phase.status === 'completed') return 'completed';
    if (phase.status === 'in_progress') return 'active';
    if (phase.status === 'blocked') return 'blocked';
    return 'locked';
  };

  const completedCount = checklist.filter((c) => c.is_completed).length;
  const totalCount = checklist.length;
  const completionPct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  // Phase config handlers
  const loadPhaseConfig = async () => {
    if (!selectedClientId) return;
    try {
      const res = await phaseAPI.getConfig(selectedClientId);
      setPhaseConfig(res.data);
      const edits = {};
      res.data.phases.forEach(p => {
        edits[p.phase_type] = {
          label: p.label,
          description: p.description,
          is_enabled: p.is_enabled,
          custom_notes: p.custom_notes || '',
        };
      });
      setConfigEdits(edits);
    } catch (error) {
      toast.error('Failed to load phase config');
    }
  };

  const handleOpenConfig = async () => {
    await loadPhaseConfig();
    setIsConfigOpen(true);
  };

  const handleSaveConfig = async () => {
    try {
      await phaseAPI.updateConfig(selectedClientId, { phases: configEdits });
      toast.success('Phase configuration saved!');
      setIsConfigOpen(false);
      loadPhases(selectedClientId);
    } catch (error) {
      toast.error('Failed to save configuration');
    }
  };

  const handleRegenerateChecklist = async (phaseId) => {
    if (!window.confirm('This will delete existing checklist items and recreate from templates. Continue?')) return;
    try {
      const res = await phaseAPI.regenerateChecklist(phaseId);
      toast.success(res.data.message);
      if (selectedPhase?.id === phaseId) loadPhaseData(phaseId);
    } catch (error) {
      toast.error('Failed to regenerate checklist');
    }
  };

  const handleRegenerateTasks = async (phaseId) => {
    if (!window.confirm('This will delete existing tasks and recreate from templates. Continue?')) return;
    try {
      const res = await phaseAPI.regenerateTasks(phaseId);
      toast.success(res.data.message);
      if (selectedPhase?.id === phaseId) loadPhaseData(phaseId);
    } catch (error) {
      toast.error('Failed to regenerate tasks');
    }
  };

  const getPhaseDescription = (phaseType) => {
    if (phaseConfig?.phases) {
      const p = phaseConfig.phases.find(pp => pp.phase_type === phaseType);
      if (p?.description) return p.description;
    }
    const meta = phaseMetadata.find(m => m.phase_type === phaseType);
    return meta?.description || '';
  };

  const getPhaseConfigLabel = (phaseType) => {
    if (phaseConfig?.phases) {
      const p = phaseConfig.phases.find(pp => pp.phase_type === phaseType);
      if (p?.label) return p.label;
    }
    return getPhaseLabel(phaseType);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="phase-management-page">
      {/* Header */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-[#0f4c81] via-[#1a6cb4] to-[#2196F3] p-6 text-white">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage:'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.2) 0%, transparent 50%)'}} />
        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold">
              {isClient() ? 'My Accreditation Progress' : 'NABH Phase Management'}
            </h1>
            <p className="text-blue-100">
              {isClient()
                ? 'Track your accreditation journey'
                : 'Manage phases, checklists, and client progress'}
            </p>
          </div>
          {!isClient() && clients.length > 0 && (
            <div className="flex items-center gap-2">
              <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                <SelectTrigger className="w-72 bg-white/10 border-white/30 text-white" data-testid="phase-client-select">
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.organization_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isAdmin() && selectedClientId && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleOpenConfig}
                  className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                  data-testid="phase-config-btn"
                >
                  <Settings className="w-4 h-4 mr-1" />
                  Configure
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Phase Timeline */}
      <Card data-testid="phase-timeline">
        <CardHeader>
          <CardTitle className="text-lg">Accreditation Journey</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between overflow-x-auto pb-2 gap-1">
            {phases.map((phase, idx) => {
              const status = getPhaseStatus(phase);
              const Icon = PHASE_ICONS[phase.phase_type] || Circle;
              const isSelected = selectedPhase?.id === phase.id;
              const availableStatuses = getAvailableStatuses(phase);
              return (
                <React.Fragment key={phase.phase_type || phase.id || idx}>
                  <div className="flex flex-col items-center min-w-[130px]">
                    <button
                      onClick={() => setSelectedPhase(phase)}
                      className={`flex flex-col items-center gap-2 w-full p-3 rounded-lg transition-all ${
                        isSelected ? 'bg-primary/10 ring-2 ring-primary' : 'hover:bg-slate-50'
                      } ${status === 'locked' ? 'opacity-60' : ''}`}
                      data-testid={`phase-btn-${phase.phase_type}`}
                    >
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          status === 'completed'
                            ? 'bg-green-100 text-green-700'
                            : status === 'active'
                            ? 'bg-primary/20 text-primary'
                            : status === 'blocked'
                            ? 'bg-red-100 text-red-600'
                            : 'bg-slate-100 text-slate-400'
                        }`}
                      >
                        {status === 'completed' ? (
                          <CheckCircle2 className="w-5 h-5" />
                        ) : status === 'blocked' ? (
                          <AlertTriangle className="w-4 h-4" />
                        ) : status === 'locked' ? (
                          <Lock className="w-4 h-4" />
                        ) : (
                          <Icon className="w-5 h-5" />
                        )}
                      </div>
                      <span className="text-xs font-medium text-center leading-tight">
                        {getPhaseConfigLabel(phase.phase_type)}
                      </span>
                      <Badge
                        variant="outline"
                        className={`text-xs ${
                          status === 'completed'
                            ? 'border-green-300 text-green-700'
                            : status === 'active'
                            ? 'border-primary text-primary'
                            : status === 'blocked'
                            ? 'border-red-300 text-red-600'
                            : 'border-slate-200 text-slate-400'
                        }`}
                      >
                        {phase.completion_percentage || 0}%
                      </Badge>
                    </button>

                    {/* Status change button — only for admins/staff with available transitions */}
                    {canChangeStatus() && availableStatuses.length > 0 && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-1 text-xs h-7 px-2"
                            data-testid={`change-status-${phase.phase_type}`}
                          >
                            Change Status
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="center">
                          {availableStatuses.map((opt) => {
                            const OptIcon = opt.icon;
                            return (
                              <DropdownMenuItem
                                key={opt.value}
                                onClick={() => {
                                  setSelectedPhase(phase);
                                  setNewStatus(opt.value);
                                  setStatusNote('');
                                  setIsStatusChangeOpen(true);
                                }}
                                data-testid={`status-option-${opt.value}`}
                              >
                                <OptIcon className={`w-4 h-4 mr-2 ${opt.color}`} />
                                {opt.label}
                              </DropdownMenuItem>
                            );
                          })}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}

                    {/* Client view: read-only status badge */}
                    {isClient() && (
                      <Badge className={`mt-1 text-xs ${getStatusColor(phase.status)}`}>
                        {phase.status?.replace('_', ' ')}
                      </Badge>
                    )}
                  </div>
                  {idx < phases.length - 1 && (
                    <ChevronRight className="w-5 h-5 text-slate-300 shrink-0 mt-[-20px]" />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* RBAC Legend */}
          {!isClient() && (
            <div className="flex items-center gap-4 mt-4 pt-3 border-t text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-slate-400" /> Pending
              </span>
              <span className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-blue-500" /> In Progress
              </span>
              <span className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-green-500" /> Completed
              </span>
              <span className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-red-500" /> Blocked
              </span>
              {isAdmin() && (
                <Badge variant="outline" className="text-xs ml-auto">
                  Admin: Can override any phase status
                </Badge>
              )}
              {isStaff() && (
                <Badge variant="outline" className="text-xs ml-auto">
                  Staff: Sequential progression only
                </Badge>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Phase Detail */}
      {selectedPhase && (
        <>
          {/* Phase Description */}
          {getPhaseDescription(selectedPhase.phase_type) && (
            <Card data-testid="phase-description">
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <BookOpen className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                  <div>
                    <h3 className="font-semibold text-sm mb-1">{getPhaseConfigLabel(selectedPhase.phase_type)}</h3>
                    <p className="text-sm text-muted-foreground">{getPhaseDescription(selectedPhase.phase_type)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Checklist */}
          <Card className="lg:col-span-2" data-testid="phase-checklist">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">
                    {getPhaseConfigLabel(selectedPhase.phase_type)} Checklist
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    {completedCount} of {totalCount} items completed
                  </p>
                </div>
                {!isClient() && (
                  <div className="flex gap-2">
                    {isAdmin() && checklist.length > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRegenerateChecklist(selectedPhase.id)}
                        data-testid="regenerate-checklist-btn"
                        title="Delete and regenerate checklist from latest templates"
                      >
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Regenerate
                      </Button>
                    )}
                    {checklist.length === 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleGenerateChecklist}
                        data-testid="generate-checklist-btn"
                      >
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Generate
                      </Button>
                    )}
                    <Button size="sm" onClick={() => setIsAddItemOpen(true)} data-testid="add-checklist-item-btn">
                      <Plus className="w-4 h-4 mr-1" />
                      Add Item
                    </Button>
                  </div>
                )}
              </div>
              <Progress value={completionPct} className="mt-3" />
            </CardHeader>
            <CardContent>
              {checklistLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : checklist.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <ClipboardList className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>No checklist items yet</p>
                  {!isClient() && <p className="text-sm">Click "Generate" to create standard checklist</p>}
                </div>
              ) : (
                <div className="space-y-2">
                  {checklist.map((item) => (
                    <div
                      key={item.id}
                      className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                        item.is_completed ? 'bg-green-50/50 border-green-200' : 'hover:bg-slate-50'
                      }`}
                      data-testid={`checklist-item-${item.id}`}
                    >
                      <Checkbox
                        checked={item.is_completed}
                        onCheckedChange={() => handleToggleChecklist(item)}
                        disabled={isClient() || selectedPhase.status === 'pending'}
                        className="mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        <p
                          className={`text-sm font-medium ${
                            item.is_completed ? 'line-through text-muted-foreground' : ''
                          }`}
                        >
                          {item.title}
                        </p>
                        {item.description && (
                          <p className="text-xs text-muted-foreground mt-0.5">{item.description}</p>
                        )}
                        {item.completed_at && (
                          <p className="text-xs text-green-600 mt-1">
                            Completed {formatDate(item.completed_at)}
                          </p>
                        )}
                      </div>
                      <Badge variant="outline" className="text-xs shrink-0">
                        {item.category}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Phase Summary & Tasks */}
          <div className="space-y-6">
            <Card data-testid="phase-summary">
              <CardHeader>
                <CardTitle className="text-lg">Phase Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-slate-50 rounded-lg text-center">
                    <p className="text-2xl font-bold">{completionPct}%</p>
                    <p className="text-xs text-muted-foreground">Completion</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg text-center">
                    <p className="text-2xl font-bold">
                      {tasks.filter((t) => t.status === 'completed').length}/{tasks.length}
                    </p>
                    <p className="text-xs text-muted-foreground">Tasks Done</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <Badge className={getStatusColor(selectedPhase.status)}>
                    {selectedPhase.status?.replace('_', ' ')}
                  </Badge>
                </div>
                {selectedPhase.started_at && (
                  <div>
                    <p className="text-sm text-muted-foreground">Started</p>
                    <p className="text-sm font-medium">{formatDate(selectedPhase.started_at)}</p>
                  </div>
                )}
                {selectedPhase.completed_at && (
                  <div>
                    <p className="text-sm text-muted-foreground">Completed</p>
                    <p className="text-sm font-medium">{formatDate(selectedPhase.completed_at)}</p>
                  </div>
                )}
                {selectedPhase.notes && (
                  <div>
                    <p className="text-sm text-muted-foreground">Notes</p>
                    <p className="text-sm">{selectedPhase.notes}</p>
                  </div>
                )}

                {/* Quick status change buttons for Admin */}
                {isAdmin() && selectedPhase.status !== 'completed' && (
                  <div className="pt-2 space-y-2">
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Quick Actions</p>
                    {selectedPhase.status === 'pending' && (
                      <Button
                        size="sm"
                        className="w-full"
                        onClick={() => {
                          setNewStatus('in_progress');
                          setStatusNote('');
                          setIsStatusChangeOpen(true);
                        }}
                        data-testid="start-phase-btn"
                      >
                        <Play className="w-4 h-4 mr-2" />
                        Start Phase
                      </Button>
                    )}
                    {selectedPhase.status === 'in_progress' && (
                      <Button
                        size="sm"
                        className="w-full"
                        onClick={() => {
                          setNewStatus('completed');
                          setStatusNote('');
                          setIsStatusChangeOpen(true);
                        }}
                        data-testid="complete-phase-btn"
                      >
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Complete Phase
                      </Button>
                    )}
                    {selectedPhase.status === 'blocked' && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full"
                        onClick={() => {
                          setNewStatus('in_progress');
                          setStatusNote('');
                          setIsStatusChangeOpen(true);
                        }}
                        data-testid="unblock-phase-btn"
                      >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Resume Phase
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Phase Tasks */}
            <Card data-testid="phase-tasks">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Phase Tasks</CardTitle>
                  {isAdmin() && selectedPhase && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRegenerateTasks(selectedPhase.id)}
                      data-testid="regenerate-tasks-btn"
                      title="Delete and regenerate tasks from latest templates"
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Regen
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {tasks.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No tasks</p>
                ) : (
                  <div className="space-y-2">
                    {tasks.slice(0, 6).map((task) => (
                      <div key={task.id} className="flex items-center gap-2 p-2 rounded border">
                        {task.status === 'completed' ? (
                          <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                        ) : (
                          <Circle className="w-4 h-4 text-slate-300 shrink-0" />
                        )}
                        <span
                          className={`text-sm truncate ${
                            task.status === 'completed' ? 'line-through text-muted-foreground' : ''
                          }`}
                        >
                          {task.title}
                        </span>
                      </div>
                    ))}
                    {tasks.length > 6 && (
                      <p className="text-xs text-muted-foreground text-center pt-1">
                        +{tasks.length - 6} more tasks
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
        </>
      )}

      {/* Status Change Confirmation Dialog */}
      <Dialog open={isStatusChangeOpen} onOpenChange={setIsStatusChangeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Phase Status</DialogTitle>
            <DialogDescription>
              Update the status of "{selectedPhase ? getPhaseLabel(selectedPhase.phase_type) : ''}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Current Status</p>
                  <Badge className={getStatusColor(selectedPhase?.status)}>
                    {selectedPhase?.status?.replace('_', ' ')}
                  </Badge>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm text-muted-foreground">New Status</p>
                  <Badge className={getStatusColor(newStatus)}>
                    {newStatus?.replace('_', ' ') || 'Select'}
                  </Badge>
                </div>
              </div>
            </div>
            {!newStatus && (
              <div className="space-y-2">
                <Label>Select new status</Label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger data-testid="status-select">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedPhase &&
                      getAvailableStatuses(selectedPhase).map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                value={statusNote}
                onChange={(e) => setStatusNote(e.target.value)}
                rows={2}
                placeholder="Add a note about this status change..."
                data-testid="status-note-input"
              />
            </div>
            {newStatus === 'completed' && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                Completing this phase will automatically start the next phase and generate its tasks and checklist.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsStatusChangeOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleStatusChange} disabled={!newStatus} data-testid="confirm-status-change-btn">
              Confirm Change
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Checklist Item Dialog */}
      <Dialog open={isAddItemOpen} onOpenChange={setIsAddItemOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Checklist Item</DialogTitle>
            <DialogDescription>Add a new item to the phase checklist</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddChecklistItem} className="space-y-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={newItem.title}
                onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                required
                data-testid="checklist-item-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={newItem.description}
                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select
                value={newItem.category}
                onValueChange={(v) => setNewItem({ ...newItem, category: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="assessment">Assessment</SelectItem>
                  <SelectItem value="documentation">Documentation</SelectItem>
                  <SelectItem value="planning">Planning</SelectItem>
                  <SelectItem value="compliance">Compliance</SelectItem>
                  <SelectItem value="manpower">Manpower</SelectItem>
                  <SelectItem value="quality">Quality</SelectItem>
                  <SelectItem value="hr">HR</SelectItem>
                  <SelectItem value="legal">Legal</SelectItem>
                  <SelectItem value="infrastructure">Infrastructure</SelectItem>
                  <SelectItem value="lab">Lab / Diagnostics</SelectItem>
                  <SelectItem value="governance">Governance</SelectItem>
                  <SelectItem value="implementation">Implementation</SelectItem>
                  <SelectItem value="application">Application</SelectItem>
                  <SelectItem value="nc_closure">NC Closure</SelectItem>
                  <SelectItem value="training">Training</SelectItem>
                  <SelectItem value="evidence">Evidence</SelectItem>
                  <SelectItem value="remediation">Remediation</SelectItem>
                  <SelectItem value="support">Support</SelectItem>
                  <SelectItem value="review">Review</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsAddItemOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="submit-checklist-item-btn">Add Item</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Phase Configuration Dialog (Admin Only) */}
      <Dialog open={isConfigOpen} onOpenChange={setIsConfigOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Phase Configuration</DialogTitle>
            <DialogDescription>
              Customize phase structure for {phaseConfig?.client_name || 'this client'}. Changes apply only to this client.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {PHASE_ORDER.map((pt) => {
              const edit = configEdits[pt] || {};
              return (
                <Card key={pt} className="border">
                  <CardContent className="pt-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-sm">{getPhaseLabel(pt)}</span>
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-muted-foreground">Enabled</label>
                        <Checkbox
                          checked={edit.is_enabled !== false}
                          onCheckedChange={(checked) =>
                            setConfigEdits(prev => ({
                              ...prev,
                              [pt]: { ...prev[pt], is_enabled: checked }
                            }))
                          }
                          data-testid={`config-enable-${pt}`}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Custom Label (leave blank for default)</Label>
                      <Input
                        value={edit.label || ''}
                        onChange={(e) =>
                          setConfigEdits(prev => ({
                            ...prev,
                            [pt]: { ...prev[pt], label: e.target.value }
                          }))
                        }
                        placeholder={getPhaseLabel(pt)}
                        data-testid={`config-label-${pt}`}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Description</Label>
                      <Textarea
                        value={edit.description || ''}
                        onChange={(e) =>
                          setConfigEdits(prev => ({
                            ...prev,
                            [pt]: { ...prev[pt], description: e.target.value }
                          }))
                        }
                        rows={2}
                        placeholder="Phase description..."
                        data-testid={`config-desc-${pt}`}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Custom Notes for Client</Label>
                      <Textarea
                        value={edit.custom_notes || ''}
                        onChange={(e) =>
                          setConfigEdits(prev => ({
                            ...prev,
                            [pt]: { ...prev[pt], custom_notes: e.target.value }
                          }))
                        }
                        rows={1}
                        placeholder="Special notes for this client's phase..."
                        data-testid={`config-notes-${pt}`}
                      />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setIsConfigOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveConfig} data-testid="save-phase-config-btn">
              Save Configuration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PhaseManagementPage;
